import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { TourId } from 'webapp/enums/tour';

import CommonFocusOverlayComponent from 'webapp/components/common/focus-overlay';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

export default class CommonPopupsPopupBlueBubble extends PopupInnerContextComponent<PopupData.Tour.BlueBubble> {
	@service('account') declare accountService: Service.Account;

	get focusOverlayElement(): HTMLElement | undefined {
		return this.popupData.focusOverlay?.el || this.popupData.attachTo;
	}

	get step(): number {
		return this.popupData.step || 1;
	}

	get type(): string {
		return this.popupData.type || '';
	}

	get showFocusOverlay() {
		return this.popupData.focusOverlay?.show && this.popupData.attachTo;
	}

	get isCustomMetricsPopupQbType(): boolean {
		return this.type === TourId.CUSTOM_METRICS_POPUP_QB;
	}

	get isCustomMetricsPopupScriptType(): boolean {
		return this.type === TourId.CUSTOM_METRICS_POPUP_SCRIPT;
	}

	get isQbSheetsType(): boolean {
		return this.type === TourId.QB_SHEETS;
	}

	get isClientPerformanceType(): boolean {
		return this.type === TourId.CLIENT_PERFORMANCE;
	}

	get isFirstBoardViewType(): boolean {
		return this.type === TourId.FIRST_BOARD_VIEW;
	}

	get isFirstBoardEditType(): boolean {
		return this.type === TourId.FIRST_BOARD_EDIT;
	}

	get isFirstBoardShareType(): boolean {
		return this.type === TourId.FIRST_BOARD_SHARE;
	}

	get isMetricsSourcesType(): boolean {
		return this.type === TourId.METRICS_SOURCES;
	}

	get isMetricsAddBlockType(): boolean {
		return this.type === TourId.METRICS_ADD_BLOCK;
	}

	get isOnboardingSourceType(): boolean {
		return this.type === TourId.ONBOARDING_SOURCE;
	}

	get isClientsSetupType(): boolean {
		return this.type === TourId.CLIENTS_SETUP;
	}

	get isReportsCreateNewSlideType(): boolean {
		return this.type === TourId.REPORTS__CREATE_NEW_SLIDE;
	}

	get isReportsAddNoteType(): boolean {
		return this.type === TourId.REPORTS__ADD_NOTE;
	}

	get isReportsReorderSlidesType(): boolean {
		return this.type === TourId.REPORTS__REORDER_SLIDES;
	}

	get isReportsDownloadPdfTourType(): boolean {
		return this.type === TourId.REPORTS__DOWNLOAD_PDF;
	}

	get isReportsFirstPreviewType(): boolean {
		return this.type === TourId.REPORTS__FIRST_PREVIEW;
	}

	get isReportsPreviewPopupEditTourType(): boolean {
		return this.type === TourId.REPORTS__PREVIEW_POPUP_EDIT;
	}

	get isReportsPreviewPopupShareTourType(): boolean {
		return this.type === TourId.REPORTS__PREVIEW_POPUP_SHARE;
	}

	get isReportsFavoriteType(): boolean {
		return this.type === TourId.REPORTS__FAVORITE;
	}

	get isReportsPresentType(): boolean {
		return this.type === TourId.REPORTS__PRESENT;
	}

	get isReportsPagelessAddBlocksType(): boolean {
		return this.type === TourId.REPORTS__PAGELESS_ADD_BLOCKS;
	}

	get isReportsPagelessAddColumnType(): boolean {
		return this.type === TourId.REPORTS__PAGELESS_ADD_COLUMN;
	}

	get isReportsPagelessCustomizeType(): boolean {
		return this.type === TourId.REPORTS__PAGELESS_CUSTOMIZE;
	}

	get isReportsPagelessShareType(): boolean {
		return this.type === TourId.REPORTS__PAGELESS_SHARE;
	}

	get isReportsPagelessViewSwitchType(): boolean {
		return this.type === TourId.REPORTS__PAGELESS_VIEW_SWITCH;
	}

	get isDesignerAddToLoopDataboardType(): boolean {
		return this.type === TourId.DESIGNER__ADD_TO_LOOP;
	}

	get isDesignerAddToReportType(): boolean {
		return this.type === TourId.DESIGNER__ADD_TO_REPORT;
	}

	get isDesignerChangeDateRangeType(): boolean {
		return this.type === TourId.DESIGNER__CHANGE_DATE_RANGE;
	}

	get isDesignerDragAndDropType(): boolean {
		return this.type === TourId.DESIGNER__DRAG_N_DROP;
	}

	get isDesignerMoveBlockType(): boolean {
		return this.type === TourId.DESIGNER__MOVE_BLOCK;
	}

	get isDesignerResizeBlockType(): boolean {
		return this.type === TourId.DESIGNER__RESIZE_BLOCK;
	}

	get isDesignerSelectDimensionTour(): boolean {
		return this.type === TourId.DESIGNER__SELECT_DIMENSION;
	}

	get isDesignerSelectVisualizationTypeType(): boolean {
		return this.type === TourId.DESIGNER__SELECT_VISUALIZATION;
	}

	get isDesignerOptionHiddenType(): boolean {
		return this.type === TourId.DESIGNER__BOARD_MORE_MENU;
	}

	get isDesignerSharableLinkType(): boolean {
		return this.type === TourId.DESIGNER__SHARABLE_LINK;
	}

	get isDesignerMoreOptionType(): boolean {
		return this.type === TourId.DESIGNER__MORE_OPTION;
	}

	get isReportsPlansGrowthType(): boolean {
		return this.type === TourId.REPORTS__PLAN_PERFORMER;
	}

	get isReportsAddDataStoryTour(): boolean {
		return this.type === TourId.REPORTS__ADD_DATA_STORY;
	}

	get isReportsSelectDataStoryTour(): boolean {
		return this.type === TourId.REPORTS__SELECT_DATA_STORY;
	}

	get isReportsChangeDataStorySettingsTour(): boolean {
		return this.type === TourId.REPORTS__CHANGE_DATA_STORY_SETTINGS;
	}

	get isReportsAddDataElementTour(): boolean {
		return this.type === TourId.REPORTS__ADD_DATA_ELEMENT;
	}

	get isReportsSelectDataElementTour(): boolean {
		return this.type === TourId.REPORTS__SELECT_DATA_ELEMENT;
	}

	get isOnboardingConnectSourceTour(): boolean {
		return this.type === TourId.ONBOARDING_CONNECT_SOURCE;
	}

	get isGuidedOnboardingMetricsTour(): boolean {
		return this.type === TourId.GUIDED_ONBOARDING__METRICS;
	}

	get isGuidedOnboardingWizardTour(): boolean {
		return this.type === TourId.GUIDED_ONBOARDING__WIZARD;
	}

	get isGuidedOnboardingDataManagerSelectedTour(): boolean {
		return this.type === TourId.GUIDED_ONBOARDING__DATA_MANAGER_SELECTED;
	}

	get isGuidedOnboardingDataManagerPopularTour(): boolean {
		return this.type === TourId.GUIDED_ONBOARDING__DATA_MANAGER_POPULAR;
	}

	get isRecentlyDeletedTour(): boolean {
		return this.type === TourId.RECENTLY_DELETED;
	}

	get isStep1(): boolean {
		return this.step === 1;
	}

	get isStep2(): boolean {
		return this.step === 2;
	}

	get isStep3(): boolean {
		return this.step === 3;
	}

	get isStep4(): boolean {
		return this.step === 4;
	}

	get isStep5(): boolean {
		return this.step === 5;
	}

	get isStep6(): boolean {
		return this.step === 6;
	}

	get isStep7(): boolean {
		return this.step === 7;
	}

	get isStep8(): boolean {
		return this.step === 8;
	}

	@action
	explicitClose(): void {
		this.popupData.onExplicitClose();
		super.close();
	}

	@action
	specialAction(...args: unknown[]): void {
		this.popupData.doAction?.(...args);
	}
	<template>
		{{#if this.showFocusOverlay}}
			<CommonFocusOverlayComponent
				@element={{this.focusOverlayElement}}
				@offset={{this.popupData.focusOverlay.offset}}
			/>
		{{/if}}
		<a role="button" class="close" {{on "click" this.explicitClose}}>
			<CommonUiIconComponent @name="Close" @size="sm" />
		</a>
		<div class="context-ct type-{{this.popupData.type}} step-{{this.step}}">
			{{#if this.isQbSheetsType}}
				{{#if this.isStep1}}
					<p>
						Select a
						<strong>column, row or cell range</strong>
						with your numerical values. When the input is selected you can
						<strong>make a selection directly on the spreadsheet below</strong>.
					</p>
					<img
						src="/images/qb-sheets-helper-animation-value.gif"
						width="150"
						height="95"
						alt
					/>
				{{else if this.isStep2}}
					<p>
						Select a column, row or cell range with
						<strong>dates or timestamps</strong>
						in it. Again, when this input is selected, you can make a selection directly
						on the spreadsheet below.
					</p>
					<img
						src="/images/qb-sheets-helper-animation-date.gif"
						width="150"
						height="95"
						alt
					/>
				{{/if}}
				<div class="actions">
					{{#unless this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins}}
						<a
							class="special-action"
							href="https://help.databox.com/create-a-custom-metric-for-google-sheets"
							target="_blank"
						>
							Learn more
						</a>
					{{/unless}}
					<a role="button" class="next-button" {{on "click" this.specialAction}}>
						OK, next
						<CommonUiIconComponent @name="ChevronRight" @size="xs" />
					</a>
				</div>
			{{else if this.isClientPerformanceType}}
				<strong>Start here</strong>
				<p>
					Start by selecting metrics for each column. We’ll display data for every account
					for each of the metrics you select.
				</p>
				<br />
				<a role="button" {{on "click" this.explicitClose}}>Ok, dismiss</a>
			{{else if this.isFirstBoardViewType}}
				<strong>We created a dashboard for you, check it out!</strong>
				<p>You can preview, edit or share dashboards with your team.</p>
			{{else if this.isFirstBoardEditType}}
				<strong>Customize your dashboard</strong>
				<p>
					Add metrics from multiple data sources, easily edit and/or rearrange your
					visualizations with the drag-and-drop Designer-- no coding or design skills
					necessary.
				</p>
				<br />
				<a role="button" {{on "click" this.explicitClose}}>Ok, dismiss</a>
			{{else if this.isFirstBoardShareType}}
				<strong>Share with your team</strong>
				<p>
					Share with others by email or a live URL. You can also schedule snapshots of
					this dashboard to automatically send to others every day, week, month, or
					quarter.
				</p>
				<br />
				<a role="button" {{on "click" this.explicitClose}}>Ok, dismiss</a>
			{{else if this.isMetricsSourcesType}}
				<strong>Explore other data sources</strong>
				<p>
					View the metrics available for each data source. Add metrics directly to new and
					existing dashboards.
				</p>
			{{else if this.isMetricsAddBlockType}}
				<strong>Add this metric to a dashboard</strong>
				<p>Want to have this metric on one of your dashboards?</p>
			{{else if this.isOnboardingSourceType}}
				<strong>Click here to continue</strong>
			{{else if this.isClientsSetupType}}
				<strong>Start here</strong>
				<p>Switch to the account and set it up by following the steps.</p>
			{{else if this.isReportsCreateNewSlideType}}
				<strong>Create a new slide</strong>
				<br />
				<img src="/images/reports/tour/new-slide.gif" width="100%" height="auto" alt />
			{{else if this.isReportsAddNoteType}}
				<strong>Add speaker notes</strong>
				<p>Type your notes here to view them while presenting.</p>
			{{else if this.isReportsReorderSlidesType}}
				<strong>Reorder slides</strong>
				<br />
				<img src="/images/reports/tour/reorder.gif" width="100%" height="auto" alt />
			{{else if this.isReportsDownloadPdfTourType}}
				<strong>Download PDF of the report</strong>
				<p>Download the PDF of the report or protect the PDF report with a password.</p>
			{{else if this.isReportsFirstPreviewType}}
				<strong>Preview your report</strong>
			{{else if this.isReportsPreviewPopupEditTourType}}
				<strong>Customize your report</strong>
				<p>Click here to edit the report before it's shared with others</p>
			{{else if this.isReportsPreviewPopupShareTourType}}
				<strong>Share with your team</strong>
				<p>Share with others by email or download the PDF report.</p>
			{{else if this.isReportsFavoriteType}}
				<strong>Add your favorite reports to your Home screen</strong>
			{{else if this.isReportsPresentType}}
				<strong>Present slides in full-screen mode</strong>
				<p>
					Switch slides using the arrow keys and access your speaker notes while
					presenting.
				</p>
			{{else if this.isReportsPagelessAddBlocksType}}
				<strong>Add blocks</strong>
				<p>Choose from metrics, dashboards, text, and more to highlight key insights.</p>
			{{else if this.isReportsPagelessAddColumnType}}
				<strong>Add columns</strong>
				<p>Organize your insights by adding up to 3 columns in your report.</p>
			{{else if this.isReportsPagelessCustomizeType}}
				<strong>Style your report</strong>
				<p>Add emojis and cover photo to make your report stand out.</p>
			{{else if this.isReportsPagelessShareType}}
				<strong>Share with stakeholders</strong>
				<p>Send via email, share a link, embed it on your CRM or website, and more.</p>
			{{else if this.isReportsPagelessViewSwitchType}}
				<strong>Switch to slides format</strong>
				<p>Turn your pageless report into a beautiful slide presentation with speaker notes.</p>
			{{else if this.isReportsAddDataStoryTour}}
				<strong>Add a Data story</strong>
			{{else if this.isReportsSelectDataStoryTour}}
				<strong>Select one of the predefined story options</strong>
			{{else if this.isReportsChangeDataStorySettingsTour}}
				<strong>Click on the element to change data settings</strong>
				<p>Changes apply to all data elements from the selected metric.</p>
			{{else if this.isReportsAddDataElementTour}}
				<strong>Add a data element to the story</strong>
			{{else if this.isReportsSelectDataElementTour}}
				<strong>The color on the Data story is the same as the color of the data element</strong>
			{{else if this.isDesignerAddToLoopDataboardType}}
				<strong>Add dashboard to the loop</strong>
				<p>Connect dashboards and loop them together into a presentation.</p>
				<br />
				<img src="/images/designer/tour/loops.gif" height="72" alt />
			{{else if this.isDesignerAddToReportType}}
				<strong>Add dashboard to the report</strong>
				<p>
					Include dashboard to the report and let your team know how your business is
					going.
				</p>
				<br />
				<img src="/images/designer/tour/report.gif" width="100%" height="auto" alt />
			{{else if this.isDesignerChangeDateRangeType}}
				<strong>Explore different date ranges for this specific metric</strong>
			{{else if this.isDesignerDragAndDropType}}
				<strong>
					Find the metric you'd like to visualize and simply drag and drop to add it to
					your dashboard.
				</strong>
				<br />
				<img src="/images/designer/tour/drag_drop.gif" width="100%" height="auto" alt />
			{{else if this.isDesignerMoveBlockType}}
				<strong>Click and drag the metric block to move it's position</strong>
				<br />
				<img src="/images/designer/tour/move.gif" width="100%" height="auto" alt />
			{{else if this.isDesignerResizeBlockType}}
				<strong>Drag the dot to resize the metric block</strong>
				<br />
				<img src="/images/designer/tour/resize.gif" width="100%" height="auto" alt />
			{{else if this.isDesignerSelectVisualizationTypeType}}
				<strong>Prefer to start with a blank visualization?</strong>
				<p>
					Simply drag and drop your favorite type to add it to your dashboard.
					<br /><br />
					You'll be able to add one or many metrics to it directly from your dashboard
				</p>
			{{else if this.isDesignerSelectDimensionTour}}
				<strong>
					Break metrics down by dimensions (like country or source) to uncover deeper
					insights.
				</strong>
				<br />
				<a role="button" class="special-action" {{on "click" this.specialAction}}>
					OK, got it!
				</a>
			{{else if this.isDesignerOptionHiddenType}}
				{{#if this.isStep1}}
					<strong>Click here for more options</strong>
				{{else if this.isStep2}}
					<strong>Click here to see all visualization types or add a metric</strong>
				{{else if this.isStep3}}
					<strong>Click here to open right panel</strong>
				{{else if this.isStep4}}
					<strong>Click here to open metric settings</strong>
				{{else if this.isStep5}}
					<strong>Click here to open metric</strong>
				{{else if this.isStep6}}
					<strong>Click here to open visualization settings</strong>
				{{else if this.isStep7}}
					<strong>Click here to open visualization type section</strong>
				{{/if}}
			{{else if this.isDesignerSharableLinkType}}
				<strong>
					Click here to get a shareable link for your dashboard & explore other options to
					send directly to email or Slack.
				</strong>
			{{else if this.isDesignerMoreOptionType}}
				<strong>
					Click here to discover a range of awesome features like snapshots, reports,
					loops and more.
				</strong>
			{{else if this.isReportsPlansGrowthType}}
				<strong>Select this plan to unlock advanced report slide types</strong>
			{{else if this.isOnboardingConnectSourceTour}}
				<strong>Click here to connect data source</strong>
			{{else if this.isGuidedOnboardingMetricsTour}}
				{{#if this.isStep1}}
					<strong>Select metrics you'd like to monitor and improve</strong>
				{{else if this.isStep2}}
					<strong>Next, let's visualize these metrics on a dashboard</strong>
				{{/if}}
			{{else if this.isGuidedOnboardingWizardTour}}
				{{#if this.isStep1}}
					<strong>
						When building your own dashboard, you can choose "Start blank" to start
						fresh, or use the wizard to get guided help setting up your dashboard.
					</strong>
					<br />
					<a role="button" class="special-action" {{on "click" this.specialAction}}>
						OK, got it!
					</a>
				{{else if this.isStep2}}
					<strong>Let's use the wizard to help us get started.</strong>
					<p>Click "Use wizard" to start.</p>
				{{else if this.isStep3}}
					<strong>Let's start with a data source you already connected</strong>
				{{else if this.isStep4}}
					<strong>Click here to continue to select metrics</strong>
				{{else if this.isStep5}}
					<strong>Select metrics you want to visualize.</strong>
					<p>
						Later, you'll be able to select native metrics from (almost) any software
						tool, spreadsheets, or build your own built metrics.
					</p>
				{{else if this.isStep6}}
					<strong>
						Continue to select the data source you want to visualize these metrics from
					</strong>
				{{else if this.isStep7}}
					<strong>Last step! Select the data source you already connected.</strong>
				{{else if this.isStep8}}
					<strong>Continue to add metrics from a second source</strong>
				{{/if}}
			{{else if this.isGuidedOnboardingDataManagerSelectedTour}}
				{{#if this.isStep1}}
					<strong>Connect the tool from which you would like to visualize data next.</strong>
				{{/if}}
			{{else if this.isGuidedOnboardingDataManagerPopularTour}}
				{{#if this.isStep1}}
					<strong>Hover over any integration and click "Connect"</strong>
				{{/if}}
			{{else if this.isRecentlyDeletedTour}}
				{{!-- inserted via css, look for popup-type-blue-bubble--recently-deleted-{{type}} --}}
				<strong>View and recover recently deleted <span class="type"></span></strong>
			{{else if this.isCustomMetricsPopupQbType}}
				<img
					src="/images/bubble-custom-metrics-popup-qb.jpg"
					width="100%"
					height="auto"
					alt
				/>
				<strong>Apply dimensions, filters, and segments</strong>
				<p>
					Create metrics like “Top Traffic by Blog Article” or “Became an Opportunity by
					Source”.
				</p>
				<br />
				<a role="button" {{on "click" this.specialAction}} class="special-action">Next</a>
			{{else if this.isCustomMetricsPopupScriptType}}
				<img
					src="/images/bubble-custom-metrics-popup-script.jpg"
					width="100%"
					height="auto"
					alt
				/>
				<strong>Calculate multiple metrics together</strong>
				<p>
					Create metrics that measure conversion rates or return on investment, like
					“Return on ad spend”.
				</p>
				<br />
				<a role="button" {{on "click" this.explicitClose}} class="special-action">Done</a>
			{{/if}}
		</div>
	</template>
}
